import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { SubscriptionService } from "../../services/Subscription.service";
import { QuizService } from "../../services/Quiz.service";
import { handleApiError } from "../../utils/handleApiError";
import { getPendingClaim, clearPendingClaim } from "../../utils/pendingClaim";
import { useI18n } from "../../i18n";
import PlanCard from "./PlanCard";

const REQUEST_STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-600",
  cancelled: "bg-gray-100 text-gray-500",
};

const MySubscription = () => {
  const { t, tr, formatDate } = useI18n();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState({ subscriptions: [], requests: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saveCard, setSaveCard] = useState(true);

  /**
   * Abunəlik yenicə aktivləşibsə və istifadəçinin gözləyən quiz nəticəsi varsa
   * (abunəlik olmadığı üçün kupon verilməmişdi) — kuponu indi alırıq.
   */
  const claimPendingQuiz = useCallback(async (isActive) => {
    const pending = getPendingClaim();
    if (!isActive || !pending?.sessionId) return;

    try {
      const res = await QuizService.claim({
        sessionId: pending.sessionId,
        guestToken: pending.guestToken,
      });
      clearPendingClaim();
      if (res.coupon) {
        toast.success(t("sub.claim_ready", { code: res.coupon.code }));
      }
    } catch {
      // Hələ alınmırsa saxlanılır — istifadəçi sonra yenidən cəhd edə bilər
    }
  }, [t]);

  const load = useCallback(async () => {
    try {
      const [sub, planList, hist] = await Promise.all([
        SubscriptionService.my(),
        SubscriptionService.plans(),
        SubscriptionService.history(),
      ]);
      setState(sub);
      setPlans(planList.plans || []);
      setHistory(hist);
      await claimPendingQuiz(sub.is_active);
    } catch (error) {
      toast.error(handleApiError(error, t));
    } finally {
      setLoading(false);
    }
  }, [claimPendingQuiz, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Bankdan qayıdış: /subscription?payment=success|failed|error
  useEffect(() => {
    const payment = params.get("payment");
    if (!payment) return;

    if (payment === "success") toast.success(t("sub.pay_success"));
    else if (payment === "failed") toast.error(t("sub.pay_failed"));
    else toast.warn(t("sub.pay_error"));

    params.delete("payment");
    setParams(params, { replace: true });
  }, [params, setParams, t]);

  const subscribe = async (plan) => {
    setBusy(true);
    try {
      const res = await SubscriptionService.subscribe({
        planId: plan.id,
        periods: 1,
        saveCard,
      });

      // Ödənişli paket → bankın ödəniş səhifəsinə yönləndir
      if (res.payment_url) {
        window.location.href = res.payment_url;
        return;
      }

      toast.success(res.message || t("sub.plan_activated"));
      await load();
    } catch (error) {
      toast.error(handleApiError(error, t));
    } finally {
      setBusy(false);
    }
  };

  const payPending = async () => {
    setBusy(true);
    try {
      const res = await SubscriptionService.pay({ saveCard });
      if (res.payment_url) {
        window.location.href = res.payment_url;
        return;
      }
      await load();
    } catch (error) {
      toast.error(handleApiError(error, t));
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    setBusy(true);
    try {
      const res = await SubscriptionService.cancelRequest();
      toast.success(res.message);
      await load();
    } catch (error) {
      toast.error(handleApiError(error, t));
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoRenew = async () => {
    setBusy(true);
    try {
      const res = await SubscriptionService.setAutoRenew(!state.auto_renew);
      toast.success(res.message);
      setState(res.subscription);
    } catch (error) {
      toast.error(handleApiError(error, t));
    } finally {
      setBusy(false);
    }
  };

  const removeCard = async () => {
    setBusy(true);
    try {
      const res = await SubscriptionService.removeCard();
      toast.success(res.message);
      setState(res.subscription);
    } catch (error) {
      toast.error(handleApiError(error, t));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const active = state?.is_active;
  const usage = state?.usage || {};

  return (
    <div className="max-w-5xl mx-auto page pt-6">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-5">
        {t("sub.my_title")}
      </h1>

      {/* ---- Cari vəziyyət ---- */}
      <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
              {t("sub.current_plan")}
            </p>
            <p className="text-2xl font-extrabold text-gray-800 mt-1">
{state?.plan ? tr(state.plan.name) : t("sub.no_plan")}
            </p>
            <p
              className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full ${
                active
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-600"
              }`}
            >
{active ? t("sub.status_active") : t("sub.status_inactive")}
            </p>
          </div>

          {state?.subscription_ends_at && (
            <div className="text-right">
<p className="text-sm text-gray-500">{t("sub.ends_at")}</p>
              <p className="text-lg font-bold text-gray-800">
{formatDate(state.subscription_ends_at)}
              </p>
              {active && state.days_left !== null && (
                <p className="text-sm text-indigo-600 font-semibold">
                  {t("common.days_left", { count: state.days_left })}
                </p>
              )}
            </div>
          )}
        </div>

        {/* İstifadə */}
        {active && (
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-indigo-50 rounded-2xl p-4">
              <p className="text-xs text-indigo-500 font-semibold uppercase">
                {t("sub.usage_quizzes")}
              </p>
              <p className="text-xl font-extrabold text-indigo-700">
                {usage.quizzes_today} / {usage.max_quizzes_per_day ?? "∞"}
              </p>
            </div>
            <div className="bg-fuchsia-50 rounded-2xl p-4">
              <p className="text-xs text-fuchsia-500 font-semibold uppercase">
                {t("sub.usage_coupons")}
              </p>
              <p className="text-xl font-extrabold text-fuchsia-700">
                {usage.coupons_this_month} / {usage.max_coupons_per_month ?? "∞"}
              </p>
            </div>
          </div>
        )}

        {/* Kart + avtomatik yenilənmə */}
        {state?.payment_method && (
          <div className="flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-gray-100">
            <span className="text-sm text-gray-600">
              {t("sub.saved_card", { mask: state.payment_method.card_mask || "****" })}
            </span>
            <button
              onClick={toggleAutoRenew}
              disabled={busy}
              className={`text-sm font-semibold px-4 py-2 rounded-xl transition disabled:opacity-60 ${
                state.auto_renew
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t("sub.auto_renew", { state: state.auto_renew ? t("sub.on") : t("sub.off") })}
            </button>
            <button
              onClick={removeCard}
              disabled={busy}
              className="text-sm font-semibold px-4 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-60"
            >
              {t("sub.remove_card")}
            </button>
          </div>
        )}
      </div>

      {/* ---- Gözləmədə olan sorğu ---- */}
      {state?.pending_request && (
        <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 mb-8">
          <p className="font-bold text-amber-800 mb-1">
            {t("sub.pending_title")}
          </p>
          <p className="text-sm text-amber-700 mb-4">
            {t("sub.pending_desc", {
              plan: state.pending_request.plan_name,
              amount: state.pending_request.amount,
              currency: state.pending_request.currency,
              periods: state.pending_request.periods,
            })}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={payPending}
              disabled={busy}
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-bold px-6 py-3 rounded-xl shadow hover:scale-[1.02] transition disabled:opacity-60"
            >
              {t("sub.complete_payment")}
            </button>
            <button
              onClick={cancelPending}
              disabled={busy}
              className="bg-white border border-amber-300 text-amber-700 font-semibold px-6 py-3 rounded-xl hover:bg-amber-100 transition disabled:opacity-60"
            >
              {t("sub.cancel_request")}
            </button>
          </div>
        </div>
      )}

      {/* ---- Paketlər ---- */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-extrabold text-white">
{active ? t("sub.change_plan") : t("sub.choose_plan")}
        </h2>
        <label className="flex items-center gap-2 text-white/90 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
            className="w-4 h-4 accent-fuchsia-500"
          />
          {t("sub.save_card")}
        </label>
      </div>

      {plans.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center text-gray-500">
          {t("sub.no_plans")}
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              current={active && state?.plan?.id === plan.id}
              loading={busy}
              onSelect={subscribe}
              ctaLabel={
                active && state?.plan?.id === plan.id ? t("sub.is_active_label") : undefined
              }
            />
          ))}
        </div>
      )}

      {/* ---- Tarixçə ---- */}
      {(history.subscriptions?.length > 0 || history.requests?.length > 0) && (
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 mt-8">
          <h3 className="text-xl font-extrabold text-gray-800 mb-4">
            {t("sub.history")}
          </h3>

          {history.subscriptions?.length > 0 && (
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b">
<th className="py-2 pr-4">{t("sub.th_plan")}</th>
<th className="py-2 pr-4">{t("sub.th_amount")}</th>
<th className="py-2 pr-4">{t("sub.th_start")}</th>
<th className="py-2 pr-4">{t("sub.th_end")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.subscriptions.map((s) => (
                    <tr key={s.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-semibold text-gray-700">
                        {tr(s.plan_name)}
                      </td>
                      <td className="py-2 pr-4">
                        {s.amount} {s.currency}
                      </td>
                      <td className="py-2 pr-4">{formatDate(s.starts_at)}</td>
                      <td className="py-2 pr-4">{formatDate(s.ends_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {history.requests?.length > 0 && (
            <div className="space-y-2">
              {history.requests.map((r) => {
                const cls = REQUEST_STATUS_STYLE[r.status] || REQUEST_STATUS_STYLE.pending;
                return (
                  <div
                    key={r.id}
                    className="flex items-center justify-between text-sm bg-gray-50 rounded-xl px-4 py-2"
                  >
                    <span className="text-gray-700">
                      #{r.id} · {r.plan_name} · {r.amount} {r.currency}
                    </span>
                    <span
                      className={`text-xs font-bold px-3 py-1 rounded-full ${cls}`}
                    >
                      {t(`sub.req_${r.status}`)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="text-center mt-8">
        <Link to="/stores" className="text-white/90 hover:text-white underline">
          {t("store.back_to_stores")}
        </Link>
      </div>
    </div>
  );
};

export default MySubscription;
